// Visão por Professor - Bloco 5
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Users, Trophy, AlertTriangle, CheckCircle, Loader2, Medal } from 'lucide-react';

const API_URL = window.location.origin;

export function VisaoProfessor({ year, trimestre }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [year, trimestre]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pendencias-academicas/por-professor?year=${year}&trimestre=${encodeURIComponent(trimestre)}`);
      const json = await res.json();
      setData(json);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!data) return null;

  const { professores, total_professores } = data;

  // Cores baseadas no percentual
  const getColor = (percentual) => {
    if (percentual >= 90) return '#10B981';
    if (percentual >= 70) return '#F59E0B';
    return '#EF4444';
  };

  // Top 10 para o gráfico
  const chartData = professores.slice(0, 10).map(p => ({
    nome: p.nome.split(' ')[0],
    percentual: p.percentual_conclusao,
    fill: getColor(p.percentual_conclusao)
  }));

  return (
    <div className="space-y-6" data-testid="visao-professor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl text-white">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">Visão por Professor</h2>
            <p className="text-slate-500 text-sm">{total_professores} professores com lançamentos</p>
          </div>
        </div>
      </div>

      {/* Gráfico de Ranking */}
      <Card className="border-purple-100">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Ranking de Conclusão
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis type="category" dataKey="nome" width={100} tick={{ fontSize: 12 }} />
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Conclusão']}
                  contentStyle={{ backgroundColor: 'white', borderRadius: '8px' }}
                />
                <Bar dataKey="percentual" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Lista Completa */}
      <Card className="border-purple-100">
        <CardHeader>
          <CardTitle className="text-lg">Detalhamento por Professor</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Professor</TableHead>
                  <TableHead className="text-center">Turmas</TableHead>
                  <TableHead className="text-center">Esperado</TableHead>
                  <TableHead className="text-center">Lançado</TableHead>
                  <TableHead className="text-center">Pendente</TableHead>
                  <TableHead className="w-[200px]">Progresso</TableHead>
                  <TableHead>Críticas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {professores.map((prof, idx) => (
                  <TableRow key={idx} className="hover:bg-purple-50/50">
                    <TableCell>
                      {prof.ranking <= 3 ? (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          prof.ranking === 1 ? 'bg-yellow-100' :
                          prof.ranking === 2 ? 'bg-slate-200' : 'bg-orange-100'
                        }`}>
                          <Medal className={`w-4 h-4 ${
                            prof.ranking === 1 ? 'text-yellow-600' :
                            prof.ranking === 2 ? 'text-slate-500' : 'text-orange-600'
                          }`} />
                        </div>
                      ) : (
                        <span className="text-slate-400">{prof.ranking}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                          {prof.nome.charAt(0)}
                        </div>
                        <div>
                          <div className="font-medium text-slate-700">{prof.nome}</div>
                          <div className="text-xs text-slate-500">{prof.disciplinas_com_pendencia} disciplinas</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{prof.turmas_com_pendencia}</Badge>
                    </TableCell>
                    <TableCell className="text-center">{prof.total_esperado}</TableCell>
                    <TableCell className="text-center text-green-600 font-medium">{prof.total_lancado}</TableCell>
                    <TableCell className="text-center text-red-600 font-bold">{prof.total_pendente}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress 
                          value={prof.percentual_conclusao} 
                          className={`h-2 flex-1 ${
                            prof.percentual_conclusao >= 90 ? '[&>div]:bg-green-500' :
                            prof.percentual_conclusao >= 70 ? '[&>div]:bg-yellow-500' : '[&>div]:bg-red-500'
                          }`}
                        />
                        <span className="text-sm font-medium w-12">{prof.percentual_conclusao}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {prof.pendencias_criticas > 0 ? (
                        <Badge className="bg-red-100 text-red-700">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {prof.pendencias_criticas}
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          OK
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
